import { type PropsWithChildren, useId, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { tv } from "tailwind-variants";

const accordionVariants = tv({
  slots: {
    trigger: [
      "flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border px-3 py-2",
      "border-line-control bg-surface text-sm text-content shadow-raised",
      "transition-colors duration-150 hover:border-content-subtle hover:bg-surface-hover",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none",
    ],
    chevron: "size-4 shrink-0 text-content-muted transition-transform duration-300",
    panel: "grid overflow-hidden transition-all duration-300 ease-in-out",
  },
  variants: {
    isOpen: {
      true: {
        panel: "grid-rows-[1fr] pt-2 opacity-100",
        chevron: "rotate-180",
      },
      false: {
        /* `invisible` on top of the zero-height row keeps collapsed content out
           of the tab order; a zero-height row alone is still focusable. */
        panel: "invisible grid-rows-[0fr] opacity-0",
      },
    },
  },
});

const { chevron, panel, trigger } = accordionVariants();

type AccordionProps = {
  title: string;
  className?: string;
};

export const Accordion = ({ children, className, title }: PropsWithChildren<AccordionProps>) => {
  const [isOpen, setIsOpen] = useState(false);

  const panelId = useId();
  const triggerId = useId();

  return (
    <div className={className}>
      <button
        aria-controls={panelId}
        aria-expanded={isOpen}
        className={trigger()}
        id={triggerId}
        onClick={() => {
          return setIsOpen((prev) => {
            return !prev;
          });
        }}
        type="button"
      >
        <span>{title}</span>

        <ChevronDownIcon aria-hidden="true" className={chevron({ isOpen })} />
      </button>

      <div aria-labelledby={triggerId} className={panel({ isOpen })} id={panelId} role="region">
        <div className="overflow-hidden">
          {/* Padding sits on the content, not the clipping wrapper, so focus
              rings have room without leaving a sliver when collapsed. */}
          <div className="px-1 pb-1 whitespace-pre-line">{children}</div>
        </div>
      </div>
    </div>
  );
};
